// Adapted from https://github.com/mui-org/material-ui/blob/ba2fce43a735f6085e68c2d76ab746a098488862/packages/mui-material/src/Skeleton/Skeleton.js.

import { clsx } from "clsx"
import { createElement, type ReactNode } from "react"

import type { SX, ThemeKeys } from "../../theme"
import styles from "./Skeleton.module.css"

export interface Props {
  component?: "span"
  borderRadius?: ThemeKeys<"borderRadius", 0 | 4 | 6 | 12 | "100%">
  color?: ThemeKeys<"color", "green4" | "grey1" | "grey5" | "purple5">
  width?: number | string
  height?: number | string
  /**
   * Alternatively to setting an explicit width and/or height, a Skeleton can take children and be implicitely sized to match it, which can improve the fidelity of the Skeleton's size.
   */
  children?: ReactNode
  sx?: SX
  /**
   * Skeleton is creating a new stacking context because it's opacity is lower than 1 during animation.
   * If you need to render an element which meets the following criteria:
   * - precedes the Skeleton in the DOM
   * - creates a new stacking context (ie. because of position: sticky)
   * and you want the Skeleton to render behind it,
   * you will need to set the zIndex to -1 on the Skeleton.
   */
  zIndex?: -1
}

export function Skeleton(props: Props): JSX.Element {
  const {
    height,
    component = "span",
    width,
    color = "grey5",
    borderRadius = 6,
    children,
    sx,
    zIndex,
  } = props

  return createElement(
    component,
    {
      className: clsx(
        styles.skeleton,
        children && styles.hasChildren,
        styles[`radius-${borderRadius}`],
        styles[`color-${color}`],
      ),
      style: {
        ...sx,
        width,
        height,
        zIndex,
      },
    },
    children,
  )
}
